/**
 * Billing Verification Panel
 * AIDLC Administrative Backend
 *
 * Compares local token tracking against Anthropic's Admin API
 * billing data. Shows per-model comparison with color-coded variances.
 *
 * States: Not Configured | Ready | Loading | Results | Error
 */

import { useState } from 'react';
import type { ReconciliationReport, ModelComparison } from '../../services/tokenService';
import { reconcileUsage } from '../../services/tokenService';

// ── Helpers ────────────────────────────────────────────────────────

function fmtCost(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function statusColor(status: 'green' | 'amber' | 'red'): string {
  if (status === 'green') return 'text-green-600 bg-green-50';
  if (status === 'amber') return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
}

function statusDot(status: 'green' | 'amber' | 'red'): string {
  if (status === 'green') return 'bg-green-500';
  if (status === 'amber') return 'bg-amber-500';
  return 'bg-red-500';
}

/** Default date range: last 7 days */
function defaultDates(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

// ── Component ──────────────────────────────────────────────────────

interface VerificationPanelProps {
  isConfigured: boolean;
}

export function VerificationPanel({ isConfigured }: VerificationPanelProps) {
  const defaults = defaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleReconcile() {
    setIsLoading(true);
    setError(null);
    setReport(null);
    try {
      const data = await reconcileUsage(startDate, endDate);
      setReport(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // Try to extract server error message from axios response
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error ?? msg);
    } finally {
      setIsLoading(false);
    }
  }

  // ── Not Configured State ──────────────────────────────────────
  if (!isConfigured) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-700 mb-1">Admin API Not Configured</p>
        <p className="text-xs text-gray-500 max-w-md mx-auto">
          Set the <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">ANTHROPIC_ADMIN_API_KEY</code> environment
          variable to enable billing verification. Generate an admin key at{' '}
          <span className="font-medium">console.anthropic.com/settings/admin-keys</span>.
        </p>
      </div>
    );
  }

  // ── Ready / Loading / Results / Error ─────────────────────────

  return (
    <div className="space-y-4">
      {/* Date Range + Run Button */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 focus:border-ldaja-blue"
          />
        </div>
        <button
          onClick={handleReconcile}
          disabled={isLoading}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Reconciling...' : 'Run Verification'}
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 py-6 justify-center">
          <div className="w-4 h-4 border-2 border-ldaja-blue border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Fetching Anthropic billing data...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      {report && <ReconciliationResults report={report} />}
    </div>
  );
}

// ── Results Sub-Component ──────────────────────────────────────────

function ReconciliationResults({ report }: { report: ReconciliationReport }) {
  const overallStatus: 'green' | 'amber' | 'red' =
    Math.abs(report.totalVariancePercent) < 1 ? 'green' :
    Math.abs(report.totalVariancePercent) < 5 ? 'amber' : 'red';

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Local Total" value={fmtCost(report.localTotalCost)} />
        <SummaryCard label="Anthropic Total" value={fmtCost(report.anthropicTotalCost)} />
        <SummaryCard
          label="Delta"
          value={fmtCost(report.totalCostDelta)}
          highlight={overallStatus !== 'green'}
        />
        <div className={`rounded-lg p-3 border border-gray-200 ${statusColor(overallStatus)}`}>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Variance</p>
          <p className="text-lg font-bold mt-1">{fmtPct(report.totalVariancePercent)}</p>
        </div>
      </div>

      {/* Per-Model Table */}
      {report.models.length > 0 && (
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                <th className="px-4 py-2 font-medium">Model</th>
                <th className="px-4 py-2 font-medium text-right">Local Tokens</th>
                <th className="px-4 py-2 font-medium text-right">Anthropic Tokens</th>
                <th className="px-4 py-2 font-medium text-right">Token Delta</th>
                <th className="px-4 py-2 font-medium text-right">Local Cost</th>
                <th className="px-4 py-2 font-medium text-right">Anthropic Cost</th>
                <th className="px-4 py-2 font-medium text-right">Cost Delta</th>
                <th className="px-4 py-2 font-medium text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {report.models.map((m) => (
                <ModelRow key={m.model} model={m} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Timestamp */}
      <p className="text-xs text-gray-400 text-right">
        Generated: {new Date(report.generatedAt).toLocaleString()}
      </p>
    </div>
  );
}

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg p-3 border border-gray-200 ${highlight ? 'bg-amber-50' : 'bg-white'}`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-lg font-bold mt-1 ${highlight ? 'text-amber-600' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function ModelRow({ model: m }: { model: ModelComparison }) {
  const localTotal = m.localInputTokens + m.localOutputTokens;
  const anthropicTotal = m.anthropicInputTokens + m.anthropicOutputTokens;

  return (
    <tr className="hover:bg-gray-50/50 transition-colors">
      <td className="px-4 py-3 font-medium text-gray-900">{m.model}</td>
      <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtTokens(localTotal)}</td>
      <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtTokens(anthropicTotal)}</td>
      <td className="px-4 py-3 text-right tabular-nums">
        <span className={m.tokenDelta !== 0 ? 'text-amber-600' : 'text-gray-500'}>
          {m.tokenDelta > 0 ? '+' : ''}{fmtTokens(m.tokenDelta)}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtCost(m.localCost)}</td>
      <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{fmtCost(m.anthropicCost)}</td>
      <td className="px-4 py-3 text-right tabular-nums">
        <span className={m.costDelta !== 0 ? 'text-amber-600' : 'text-gray-500'}>
          {fmtCost(m.costDelta)}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(m.status)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusDot(m.status)}`} />
          {fmtPct(m.variancePercent)}
        </span>
      </td>
    </tr>
  );
}
